import * as d3 from 'd3';
import { UsageReport, UsageReportItem, UsageReportItemType } from './usage-store';
import { formatNumber } from '../utils/formatter';

const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const monthShortNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

interface ChartDimensions {
    width: number;
    height: number;
    marginTop: number;
    marginRight: number;
    marginBottom: number;
    marginLeft: number;
}

export function renderLineChart(usageReport: UsageReport, container: HTMLElement, legendContainer: HTMLElement) {
    const dimensions = getChartDimensions(container);

    const svg = createSvgContainer(dimensions);

    const { xScale, yScale } = createScales(usageReport, dimensions);

    renderHorizontalAxis(svg, dimensions, xScale);
    renderVerticalAxis(svg, dimensions, yScale, usageReport);

    const points = computePoints(usageReport, xScale, yScale);
    const groups = groupPoints(points);

    renderPlannedArea(svg, dimensions, points);
    const linePath = renderLinePaths(svg, groups);

    renderTodayLine(svg, xScale, dimensions, usageReport);

    const interactiveTip = createTooltipDot(svg);
    enablePointerInteraction(svg, xScale, points, linePath, interactiveTip, dimensions, usageReport);

    appendGraphToContainer(container, svg.node());

    const legend = createLegend();
    appendLegendToContainer(legendContainer, legend.node());
}

/** Utility Functions */
function getChartDimensions(container: HTMLElement): ChartDimensions {
    return {
        width: parseInt(container.style.width),
        height: parseInt(container.style.height) + 15,
        marginTop: 20,
        marginRight: 20,
        marginBottom: 30,
        marginLeft: 30,
    };
}

function createSvgContainer(dimensions: ChartDimensions) {
    return d3.create("svg")
        .attr("width", dimensions.width)
        .attr("height", dimensions.height)
        .attr("viewBox", [0, 0, dimensions.width, dimensions.height])
        .attr("style", "max-width: 100%; height: auto; overflow: visible; font: 14px sans-serif;");
}

function createScales(usageReport: UsageReport, dimensions: ChartDimensions) {
    const xScale = d3.scaleUtc()
        .domain(d3.extent(usageReport.reportItems, (d: UsageReportItem) => d.xAxisValue))
        .range([dimensions.marginLeft, dimensions.width - dimensions.marginRight]);

    const yScale = d3.scaleLinear()
        .domain([d3.min(usageReport.reportItems, (d: UsageReportItem) => Math.round(d.cumulativeCount)), d3.max(usageReport.reportItems, (d: UsageReportItem) => Math.round(d.cumulativeCount))]).nice()
        .range([dimensions.height - dimensions.marginBottom, dimensions.marginTop]);

    return { xScale, yScale };
}

/** Rendering Functions */
function renderHorizontalAxis(svg: any, dimensions: ChartDimensions, xScale: any) {
    svg.append("g")
        .attr("transform", `translate(0,${dimensions.height - dimensions.marginBottom})`)
        .call(d3.axisBottom(xScale).ticks(d3.timeMonth.every(1)).tickFormat(d3.timeFormat("%B")));
}

function renderVerticalAxis(svg: any, dimensions: ChartDimensions, yScale: any, usageReport: UsageReport) {
    svg.append("g")
        .attr("transform", `translate(${dimensions.marginLeft},0)`)
        .call(d3.axisLeft(yScale).tickFormat((d) => {
            const max = d3.max(usageReport.reportItems, (d: UsageReportItem) => Math.round(d.cumulativeCount));
            return max >= 10000 ? `${d / 1000}k` : d.toString();
        }))
        .call(g => g.select(".domain").remove())
        .call(g => g.append("text")
            .attr("x", -dimensions.marginLeft)
            .attr("y", 0)
            .attr("fill", "currentColor")
            .attr("text-anchor", "start")
            .text("↑ Usage"));
}

function renderPlannedArea(svg: any, dimensions: ChartDimensions, points: any[][]) {
    const area = d3.area()
        .x(d => d[0])
        .y0(dimensions.height - dimensions.marginBottom)
        .y1(d => d[1]);

    svg.append("path")
        .datum(points.filter(d => d[2] === UsageReportItemType.Planned))
        .attr("fill", colorScale(UsageReportItemType.Planned))
        .attr('opacity', 0.5)
        .attr("d", area);
}

function renderLinePaths(svg: any, groups: any[][]) {
    const line = d3.line();

    return svg.append("g")
        .attr("fill", "none")
        .attr("stroke-width", 3)
        .attr("stroke-linejoin", "round")
        .attr("stroke-linecap", "round")
        .selectAll("path")
        .data(Array.from(groups.values()).filter(group => group[0][2] !== UsageReportItemType.Planned)) // Exclude Planned        
        .join("path")
        .style("mix-blend-mode", "multiply")
        .attr("stroke", d => colorScale(d[0][2]))
        .attr("stroke-dasharray", d => getLineDashArray(d[0][2]))
        .attr("d", line);
}

function renderTodayLine(svg: any, xScale, dimensions: ChartDimensions, usageReport: UsageReport) {
    const today = new Date();
    const maxDate = d3.max(usageReport.reportItems, (d: UsageReportItem) => d.xAxisValue);
    const lineDate = today > maxDate ? maxDate : today;

    svg.append("line")
        .attr("x1", xScale(lineDate))
        .attr("x2", xScale(lineDate))
        .attr("y1", dimensions.marginTop)
        .attr("y2", dimensions.height - dimensions.marginBottom)
        .attr("stroke", 'black')
        .attr("stroke-width", 0.5)
        .attr("stroke-dasharray", null);

    svg.append("text")
        .attr("x", xScale(lineDate))
        .attr("y", dimensions.marginTop - 5)
        .attr("text-anchor", "middle")
        .attr("fill", "black")
        .attr("font-size", "12px")
        .text("Today");
}

/** Interaction Functions */
function enablePointerInteraction(svg: any, xScale, points, linePath: any, tooltipDot: any, dimensions: ChartDimensions, usageReport: UsageReport) {
    const pointermoved = (event) => {
        const [xm, ym] = d3.pointer(event);
        const i = d3.leastIndex(points, ([x, y]) => Math.hypot(x - xm, y - ym));
        const [x, y, k] = points[i];
        linePath.style("stroke", ({ z }) => z === k ? null : "#ddd").filter(({ z }) => z === k).raise();
        tooltipDot.attr("transform", `translate(${x},${y})`);
        const reportItem = usageReport.reportItems[i];
        const dotText = getDotText(reportItem);
        tooltipDot.select("text").text(dotText);
        tooltipDot.select("text").dispatch("text-change"); // Trigger text change event to update background size
        svg.property("value", reportItem).dispatch("input", { bubbles: true });
    };

    const pointerentered = () => {
        linePath.style("mix-blend-mode", null).style("stroke", "#ddd");
        tooltipDot.attr("display", null);
    };

    const pointerleft = () => {
        linePath.style("mix-blend-mode", "multiply").style("stroke", null);
        tooltipDot.attr("display", "none");
        svg.node().value = null;
        svg.dispatch("input", { bubbles: true });
    };

    svg
        .on("pointerenter", pointerentered)
        .on("pointermove", pointermoved)
        .on("pointerleave", pointerleft)
        .on("touchstart", event => event.preventDefault());
}

function getDotText(reportItem: UsageReportItem) {
    if (reportItem.isCurrentItem) {
        return `${reportItem.type}: ${formatNumber(Math.round(reportItem.cumulativeCount))} (Today)`;
    }
    else if (reportItem.isStartItem) {
        return `${reportItem.type}: ${formatNumber(Math.round(reportItem.cumulativeCount))} (${formatDateForDotText(reportItem.startDate)})`;
    }
    else {
        return `${reportItem.type}: ${formatNumber(Math.round(reportItem.cumulativeCount))} (${formatDateForDotText(reportItem.endDate)})`;
    }
}

/** Helper Functions */
function formatDateForDotText(dateString: string) {
    // Check if string is empty / null / undefined
    if (!dateString) {
        return '';
    }
    var date = new Date(dateString);
    return `${monthShortNames[date.getMonth()]} ${date.getDate()}`; // e.g. "Jan 1"
}

function computePoints(usageReport: UsageReport, xScale: any, yScale: any) {
    return usageReport.reportItems.map((u: UsageReportItem) => [
        xScale(u.xAxisValue),
        yScale(Math.round(u.cumulativeCount)),
        u.type
    ]);
}

function groupPoints(points: any[][]) {
    return d3.rollup(points, v => Object.assign(v, { z: v[0][2] }), d => d[2]);
}

function createTooltipDot(svg: any) {
    const dot = svg.append("g").attr("display", "none");

    // Add a background rectangle for the tooltip text (to make text better visible on the graph background)
    const background = dot.append("rect")
        .attr("fill", "white")
        .attr("opacity", 0.9)
        .attr("rx", 4) // Rounded corners
        .attr("ry", 4);

    dot.append("circle").attr("r", 2.5);
    const text = dot.append("text").attr("text-anchor", "middle").attr("y", -8);

    // Adjust the background size dynamically based on the text
    text.on("text-change", function () {
        const bbox = text.node().getBBox();
        background
            .attr("x", bbox.x - 4) // Add padding
            .attr("y", bbox.y - 2)
            .attr("width", bbox.width + 8)
            .attr("height", bbox.height + 4);
    });

    return dot;
}

function createLegend() {
    const legend = d3.create("div")
        .attr("class", "legend")
        .style("display", "flex")
        .style("gap", "10px")
        .style("margin-bottom", "20px")
        .style("float", "right");

    [UsageReportItemType.Actual, UsageReportItemType.ActualPrevious, UsageReportItemType.Forecast, UsageReportItemType.Planned].forEach(type => {
        const legendItem = legend.append("div")
            .style("display", "flex")
            .style("align-items", "center")
            .style("gap", "5px");

        legendItem.append("svg")
            .attr("width", 20)
            .attr("height", 10)
            .append("line")
            .attr("x1", 0)
            .attr("y1", 5)
            .attr("x2", 20)
            .attr("y2", 5)
            .attr("stroke", colorScale(type))
            .attr("stroke-width", 3)
            .attr("stroke-dasharray", getLineDashArray(type));

        legendItem.append("span").text(type);
    });

    return legend;
}

const colorScale = d3.scaleOrdinal()
    .domain([UsageReportItemType.Actual, UsageReportItemType.Forecast, UsageReportItemType.Planned, UsageReportItemType.ActualPrevious])
    .range([
        '#b3d1ff',
        '#b3d1ff',
        '#f0f0f0',
        d3.color('#b3d1ff').darker(1).formatHex()
    ]);

const getLineDashArray = (usageReportItemType: UsageReportItemType) => {
    switch (usageReportItemType) {
        case UsageReportItemType.Forecast:
            return "4 4";
        default:
            return null;
    }
};

function appendGraphToContainer(container: HTMLElement, svg: SVGSVGElement) {
    container.innerHTML = '';
    container.appendChild(svg);
}

function appendLegendToContainer(legendContainer: HTMLElement, legend: HTMLElement) {
    legendContainer.innerHTML = '';
    legendContainer.appendChild(legend);
}


// WEBPACK FOOTER //
// ./ClientApp/app/services/licensing-contract-line-chart-service.ts